import * as THREE from 'three';
import { LODRaycast, MapView, MapHeightNodeShader, UnitsUtils, LODFrustum } from 'geo-three';
import { MapsProvider } from './mapProvider';
import { Text } from 'troika-three-text';
import fontUrl from '/src/css/fonts/CartographSansCF-DemiBold.woff';

import {
	BloomEffect,
	EffectComposer,
	EffectPass,
	RenderPass,
	ToneMappingEffect,
	ToneMappingMode
} from 'postprocessing';
import CameraControls from 'camera-controls';
// import MBTilesHeightProvider, { MapTilesProvider } from "./provider";
import { appState } from './stateManager';
import {
	BOUNDS,
	CAMERA_POSITION,
	CAMERA_TARGET,
	FLY_TO_ANGLE,
	FLY_TO_DISTANCE,
	START_DISTANCE,
	MAX_DISTANCE,
	SCALAR_Y,
	MARKER_POS_Y
} from './scene.details';

import '../css/scene.css';
import Markers from './markers';
import { AIR_STRIKE_OPTIONS, INTENSITIES, MARTIAL_STRUCTURE } from './details';
import { CITIES } from './cities';

CameraControls.install({ THREE: THREE });

export default class Scene {
	constructor() {
		this.pointer = new THREE.Vector2();
		this.raycaster = new THREE.Raycaster();
		this.selectedInstanceId = null;

		this.startCoords = { x: 2082535.1127763207, y: 0, z: -5954139.93682931 }; // UnitsUtils.datumsToSpherical(46.84247502771687, 16.832095157147126);
		this.mapView;
		this.composer;
		this.mouse = new THREE.Vector2();
		this.stats;
		this.is2d = false;
		this.lastInstanceId = null;
		this.matrix = new THREE.Matrix4();
		// this.data;
		this.frustum = new THREE.Frustum();
		this.cameraViewProjectionMatrix = new THREE.Matrix4();
		// document.body.appendChild(this.stats.dom);
		this.hidden = true;
		this.lastCullTime = 0;
		this.lastRaycastTime = 0;
		this.currentThresholdIndex = -1;
		this.isLowEnd = false;
		this.instancedMesh;
		this.pins;
		this.labels = [];
		this.lastControlState = false;
		this._lastHoverId = -1;
		this._prevDistance = null;
		this.id = -1;
	}

	init() {
		appState.subscribe(this.onStateChange.bind(this));

		this.setupScene();
		this.setupRenderer();
		this.setupLights();
		this.setupControls();
		this.setupPostProcessing();
		this.setupReset();
		this.setupEvents();
		this.markers = new Markers(this.scene, this.markersReady.bind(this));
		this.markers.init();
		// this.setupMap();
		this.animate();
		setTimeout(() => {
			this.zoom(-MAX_DISTANCE);
		}, 1100);
		console.log('scene init');
	}

	setupReset() {
		const resetBtn = document.getElementById('main-reset-btn');
		if (!resetBtn) return;
		resetBtn.onclick = () => {
			const currentState = appState.getState();
			appState.setState({
				...currentState,
				id: -1
			});
			this.controls.setTarget(this.startCoords.x, 0, this.startCoords.z, true);
			this.controls.setPosition(this.startCoords.x, 0, this.startCoords.z + 103000, true);
			setTimeout(() => {
				this.zoom(-MAX_DISTANCE);
			}, 1100);
		};
	}

	setupScene() {
		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color('#cbcbcb');
	}

	setupRenderer() {
		this.renderer = new THREE.WebGLRenderer({
			powerPreference: 'high-performance',
			antialias: true,
			stencil: false,
			depth: true
		});
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer.getContext().getExtension('WEBGL_depth_texture');
		this.renderer.autoClear = true;
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer.domElement.id = 'map';
		this.renderer.domElement.classList.add(Math.random().toString());
		this.mapCanvas = this.renderer.domElement;
		document.getElementById('app')?.prepend(this.renderer.domElement);
	}

	setupPostProcessing() {
		this.composer = new EffectComposer(this.renderer);

		this.renderPass = new RenderPass(this.scene, this.camera);
		this.renderPass.clear = true;
		this.composer.addPass(this.renderPass);

		this.tonePass = new EffectPass(
			this.camera,
			new ToneMappingEffect({
				mode: ToneMappingMode.NEUTRAL
			})
		);
		this.composer.addPass(this.tonePass);
	}

	setupLights() {
		// const light = new THREE.DirectionalLight(0xffffff, 1);
		// light.position.set(this.startCoords.x, 5111, -this.startCoords.y);
		// light.castShadow = true;
		// light.target.position.set(this.startCoords.x + 1111, 0, -this.startCoords.y);
		// light.shadow.mapSize.width = 2048 * 2; // Adjust for better shadow quality
		// light.shadow.mapSize.height = 2048 * 2;
		// light.shadow.bias = -0.001;
		// light.shadow.blurSamples = 4;
		// light.shadow.camera.top = 30000;
		// light.shadow.camera.bottom = -30000;
		// light.shadow.camera.left = -30000;
		// light.shadow.camera.right = 30000;
		// light.shadow.camera.near = 0.5;
		// light.shadow.camera.far = 50000;
		// this.scene.add(light);
		// const helper = new THREE.DirectionalLightHelper(
		//   light,
		//   5000,
		//   this.color.setHex(0xff0000)
		// );
		// this.scene.add( helper );
		//filllight
		const fillLight = new THREE.DirectionalLight(0xff0000, 4);
		fillLight.position.set(this.startCoords.x, -5111, -this.startCoords.y);
		fillLight.castShadow = false;
		fillLight.target.position.set(-this.startCoords.x + 1111, 0, -this.startCoords.y);
		// this.scene.add(fillLight);
		// const helper2 = new THREE.CameraHelper( light.shadow.camera );
		// this.scene.add( helper2 );
		const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 2);
		this.scene.add(hemiLight);
		// this.loadHDRTexture();
	}

	setupControls() {
		this.clock = new THREE.Clock();
		this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 100, 1200000);

		if (this.controls) {
			this.controls.dispose();
		}
		this.controls = new CameraControls(this.camera, this.renderer.domElement);
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.01;
		this.controls.truckSpeed = 2.0;
		this.controls.touches.one = CameraControls.ACTION.TRUCK; //this.controls.touches.two;
		this.controls.mouseButtons.left = CameraControls.ACTION.SCREEN_PAN;
		this.controls.mouseButtons.right = this.controls.mouseButtons.left; //CameraControls.ACTION.ROTATE;
		this.controls.minDistance = 8000;
		this.controls.maxDistance = 654333;
		this.controls.setBoundary(this.getBoundaryBox());

		this.controls.restThreshold = 0.01;
		this.controls.distance = 30;

		this.controls.setTarget(this.startCoords.x, 0, this.startCoords.z, false);
		this.controls.setPosition(this.startCoords.x, 0, this.startCoords.z + 103000, false);

		// this.controls.setTarget(this.startCoords.x, 0, -this.startCoords.y, true);
		// this.controls.setPosition(this.startCoords.x, 628000, -this.startCoords.y, false);

		this.controls.minPolarAngle = -Math.PI;
		this.controls.maxPolarAngle = 1;
		// this.controls.minAzimuthAngle = -Math.PI / 4;
		// this.controls.maxAzimuthAngle = Math.PI / 4;

		this.controls.rotatePolarTo(Math.PI / 4, true);

		//this.controls.addEventListener('controlstart', () => this.hideMarkerInfo());
		///this.controls.addEventListener('transitionstart', () => this.hideMarkerInfo());

		this.controls.addEventListener('control', () => this.userDraging());
		this.controls.addEventListener('rest', () => this.userStoppedDragging());

		this.controls.update();
	}

	getBoundaryBox() {
		const topLeft = UnitsUtils.datumsToSpherical(BOUNDS[1], BOUNDS[0]);
		const bottomRight = UnitsUtils.datumsToSpherical(BOUNDS[3], BOUNDS[2]);
		const center = new THREE.Vector3((topLeft.x + bottomRight.x) / 2, 0, -(topLeft.y + bottomRight.y) / 2);
		const size = new THREE.Vector3(
			Math.abs(bottomRight.x - topLeft.x),
			MAX_DISTANCE,
			Math.abs(topLeft.y - bottomRight.y)
		);
		return new THREE.Box3().setFromCenterAndSize(center, size);
	}

	async performRaycast() {
		// if (this.controls.distance > 240000) {
		// 	// disable hover when zoomed out
		// 	if (this._lastHoverId !== -1) {
		// 		const st = appState.getState();
		// 		if (st.hoverId !== -1) appState.setState({ ...st, hoverId: -1 });
		// 		this.markerHovered(-1);
		// 		this._lastHoverId = -1;
		// 	}
		// 	return;
		// }
		// console.log('perform');

		this.raycaster.setFromCamera(this.mouse, this.camera);
		const intersects = this.raycaster.intersectObject(this.instancedMesh);
		// console.log(intersects);
		if (intersects.length > 0) {
			const instanceId = intersects[0].instanceId;
			const marker = this.pins[instanceId];
			const id = marker?.id;
			if (!Number.isInteger(id)) return;

			if (this._lastHoverId !== id) {
				const currentState = appState.getState();
				if (currentState.hoverId !== id) {
					appState.setState({ ...currentState, hoverId: id });
				}
				this.markerHovered(id);
				this._lastHoverId = id;
			}

			if (this.isMouseClick) {
				const marker = this.pins[instanceId];
				const id = marker?.id;
				if (!Number.isInteger(id)) return;
				const currentState = appState.getState();
				if (currentState.id !== id) appState.setState({ ...currentState, id });
				this.flyToMarker(marker?.pos);
				this.lastInstanceId = instanceId;
			}
		} else {
			// raycasting on other elements than the instances = off
			if (this._lastHoverId !== -1) {
				const st = appState.getState();
				if (st.hoverId !== -1) {
					appState.setState({ ...st, hoverId: -1 });
				}
				this.markerHovered(-1);
				this._lastHoverId = -1;
			}

			// off clicking
			if (this.isMouseClick && this.lastInstanceId !== null) {
				this.lastInstanceId = null;
			}
		}
	}

	setupEvents() {
		window.addEventListener('resize', () => this.onWindowResize());
		document.addEventListener('mousemove', (e) => this.onMouseMove(e));
		document.addEventListener('mouseup', () => this.onMouseClick());
		document.addEventListener('touchend', this.onMouseClick());

		//what is this reset?
		const resetBtn = document.getElementById('reset-btn');
		if (!resetBtn) return;
		resetBtn.onclick = () => {
			this.reset();
		};

		//todo gui zoom in/out button
		this.zoomInButton.addEventListener('click', () => {
			this.zoom(45000, false);
		});
		this.resetZoom.addEventListener('click', () => {
			// this.mapView.flyToBoundingBox();
		});
		this.zoomOutButton.addEventListener('click', () => {
			this.zoom(-45000, false);
		});
	}

	setupMapEvents() {
		// Remove existing event listeners if they exist
		if (this.pointerMoveHandler) {
			this.mapCanvas.removeEventListener('pointermove', this.pointerMoveHandler);
		}
		if (this.clickHandler) {
			this.mapCanvas.removeEventListener('click', this.clickHandler);
		}

		// Define and store new event listeners
		this.pointerMoveHandler = () => {
			// console.log('pointermove');
			const now = performance.now();
			if (now - this.lastRaycastTime > 20) {
				this.performRaycast();
				this.lastRaycastTime = now;
			}
		};

		this.clickHandler = () => {
			this.isMouseClick = true;
			this.performRaycast();
			this.isMouseClick = false;
		};

		// Attach new event listeners
		this.mapCanvas.addEventListener('pointermove', this.pointerMoveHandler);
		this.mapCanvas.addEventListener('click', this.clickHandler);
	}

	onMouseClick() {
		this.isMouseClick = true;
		this.controls.smoothTime = 0.25;
		if (this.mouseClickTimeout) clearTimeout(this.mouseClickTimeout);
		this.mouseClickTimeout = setTimeout(() => {
			this.isMouseClick = false;
		}, 10);
	}

	onMouseMove(event) {
		event.preventDefault();
		this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
		this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
	}

	onWindowResize() {
		this.camera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(window.innerWidth, window.innerHeight);
	}

	reset() {
		this.controls.setTarget(CAMERA_TARGET.x, CAMERA_TARGET.y, CAMERA_TARGET.z, true);
		this.controls.setPosition(CAMERA_POSITION.x, CAMERA_POSITION.y, CAMERA_POSITION.z, true);
	}

	zoom(distance, init = true) {
		this.controls.smoothTime = 0.25;
		if (init) {
			this.controls.smoothTime = 1.5;
		}
		// console.log(this.controls.smoothTime);
		this.controls.dolly(distance, true);
		// this.controls.smoothTime = 0.25;
	}

	markersReady() {
		// console.log(this.markers.instancedMesh);
		this.instancedMesh = this.markers.instancedMesh;
		this.pins = this.markers.getMarkers();
		for (let i = 0; i < this.pins.length; i++) {
			const m = this.pins[i];
			// console.log(m);
			this.createLabel(m.placeName, m.pos.x, MARKER_POS_Y, m.pos.z);
		}
		this.setupMap();
		this.setupMapEvents();
	}

	setupMap() {
		console.log('setupmap');
		const isDev = import.meta.env.DEV;
		const isVektorlab = window.location.href?.toLowerCase().includes('vektorlab');
		const provider0 =
			isDev || isVektorlab
				? new MapsProvider('https://mapserver.vektorlab.hu/1941/tiles.php')
				: new MapsProvider('https://mo44-45.mnl.gov.hu/mapserver/1941/tiles.php/');
		const provider1 =
			isDev || isVektorlab
				? new MapsProvider('https://mapserver.vektorlab.hu/1941/tiles.php', true)
				: new MapsProvider('https://mo44-45.mnl.gov.hu/mapserver/1941/tiles.php/', true);

		this.mapView = new MapView(
			MapView.HEIGHT,
			// 'https://mo44-45.mnl.gov.hu/mapserver/1941/tiles.php/'

			provider0,
			provider1
		);
		this.mapView.lod = new LODFrustum();
		this.mapView.heightNode = MapHeightNodeShader;
		this.mapView.updateMatrixWorld(true);
		this.mapView.scale.y = SCALAR_Y;
		this.scene?.add(this.mapView);

		// this.setupMarkers(this.markers.getData());
	}

	createLabel(text, x, y, z) {
		const label = new Text();
		// 1944-45/webapp/src/css/fonts/DarkerGrotesque-Medium.ttf
		label.font = fontUrl;
		label.text = text;
		label.fontSize = 400;
		label.position.set(x, y + 2000, z);
		label.textAlign = 'center';
		label.color = 0xffffff;
		label.anchorX = '50%';
		label.sync();

		this.scene.add(label);
		this.labels.push(label);
	}

	flyToMarker(pos) {
		if (!pos) return;
		const coord = { x: pos.x, y: pos.z };
		const height = FLY_TO_DISTANCE * Math.sin(FLY_TO_ANGLE);
		// const offset = FLY_TO_DISTANCE * Math.cos(FLY_TO_ANGLE);
		const camX = coord.x; //+ offset;
		const camY = height;
		const camZ = coord.y; //+ offset;
		this.controls.setLookAt(camX, camY, camZ, coord.x, pos.y, coord.y, true);
	}

	lerpMapHeightByViewDistance(distance) {
		if (!this.mapView) return;
		const d0 = 30000;
		const d1 = 150000;

		const t = THREE.MathUtils.clamp((distance - d0) / (d1 - d0), 0, 1);

		const k = 1 - t;

		this.mapView.scale.y = THREE.MathUtils.lerp(0, SCALAR_Y, k);
	}

	lerpCameraAngleByZoom(distance) {
		const dMin = this.controls.minDistance;
		const dMax = this.controls.maxDistance;

		const t = THREE.MathUtils.clamp((distance - dMin) / (dMax - dMin), 0, 1);

		const targetPolar = THREE.MathUtils.lerp(1, -Math.PI / 4, t);

		this.controls.rotatePolarTo(targetPolar, false);
	}

	updateMarkerInstanceTransforms(distance) {
		if (!this.instancedMesh) return;
		this.markers.updateBaseTransforms(distance);
	}

	cullLabels(camera) {
		//imported and ported from 'civic associations' project
		// Build frustum
		this.cameraViewProjectionMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
		this.frustum.setFromProjectionMatrix(this.cameraViewProjectionMatrix);

		for (let i = 0; i < this.labels.length; i++) {
			const lbl = this.labels[i];
			const p = new THREE.Vector3().setFromMatrixPosition(lbl.matrixWorld);

			const isVisible = this.frustum.containsPoint(p);

			if (!isVisible) {
				lbl.visible = false;
				continue;
			}

			// hide labels when zoomed out too far
			if (this.controls.distance > 70000) {
				lbl.visible = false;
				continue;
			}

			lbl.visible = true;
		}
	}

	animate() {
		const delta = this.clock?.getDelta();
		const time = this.clock.getElapsedTime();
		const distance = this.controls.distance;

		const distanceChanged = this._prevDistance !== distance;
		this._prevDistance = distance;

		this.controls?.update(delta);
		this.updateMarkerInstanceTransforms(distance);
		this.cullLabels(this.camera);
		this.markers.animateMarkers(time);

		// if (distanceChanged) {
		this.lerpCameraAngleByZoom(distance);
		// this.lerpMapHeightByViewDistance(distance);
		// }

		this.composer.render();
		requestAnimationFrame(() => this.animate());
	}

	onStateChange(state) {
		if (state.id === this.id) return;
		this.id = state.id;
		if (this.id === -1) {
			this.zoomOutOnClose();
			return;
		}
	}

	zoomOutOnClose() {
		this.zoom(-30000, false);
	}

	userDraging() {
		document.body.className = 'dragging';
	}
	userStoppedDragging() {
		document.body.className = '';
	}
	markerHovered(id) {
		id >= 0 ? (document.body.className = 'hover') : (document.body.className = '');
	}
}
